import { RichTextEditor } from "@mantine/tiptap";
import { Editor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@headlessui/react";
import "tiptap-extension-resizable-image/styles.css";

import { cn } from "../../utils";
import Loader from "../AppLoader";
import {
    ActionsMenu,
    FontFamilySelect,
    FontSizeSelect,
    InsertImage,
    LineHeightSelect,
    LinkButton,
    SignatureReasonMenu,
    TableMenu,
} from "./components";
import "./styles.css";
import useMyEditor from "./useMyEditor";
import { getCurrentPageNumber } from "./utils";
import { useReport } from "../../hooks/useReport";
import useConfirm from "../../hooks/useConfirm";
import { useAuth } from "../../context/AuthContext";
import usePrompt from "../../hooks/usePrompt";
import useTestId from "../../hooks/useTestId";
import useAnalytics from "../../hooks/useAnalytics";

interface TiptapEditorProps {
    initialContent?: string;
    onSubmit?: (editor: Editor) => Promise<void>;
    isSubmitting?: boolean;
}

export const ReportEditor: React.FC<TiptapEditorProps> = ({
    initialContent,
    onSubmit,
    isSubmitting,
}) => {
    const { isAllowed } = useAuth();
    const { analytics, updateAnalytics } = useAnalytics();
    const { show } = useConfirm();
    const { logo, status, mutate, scale, setScale, defaultFontSize } =
        useReport();
    const testId = useTestId();
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isDirty, setIsDirty] = useState(false);
    const [withFormatting, setWithFormatting] = useState(false);
    const [
        hasShownSignedReportChangeAlert,
        setHasShownSignedReportChangeAlert,
    ] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const editor = useMyEditor({
        content: initialContent,
        headerLogo: logo,
        onUpdate: ({ editor, transaction }) => {
            if (transaction.getMeta("pagination")) {
                return;
            }
            const _onUpdate = () => {
                setIsDirty(true);
                const currentPageNumber = getCurrentPageNumber(editor);
                setCurrentPage(currentPageNumber);
            };

            if (status === "Signed" && !hasShownSignedReportChangeAlert) {
                show({
                    title: "Note that you edit a signed report",
                    description: "",
                    confirmText: "Continue Editing",
                    cancelText: "Cancel",
                    onConfirm: () => {
                        _onUpdate();
                        setHasShownSignedReportChangeAlert(true);
                    },
                    onCancel: () => {
                        editor.commands.undo();
                        setIsDirty(false);
                    },
                });
            }
            _onUpdate();
        },
        onTransaction: ({ editor }) => {
            const currentPageNumber = getCurrentPageNumber(editor);
            setCurrentPage(currentPageNumber);
        },
        editable: isAllowed("Report edit"),
        options: {
            defaultFontSize,
        },
    });

    useEffect(() => {
        if (initialContent && editor) {
            editor.commands.setContent_force(initialContent);
            editor?.commands.triggerPagination();
        }
    }, [initialContent, editor]);

    useEffect(() => {
        if (editor) {
            editor.storage.scale = scale;
        }
    }, [scale]);

    // Scroll position management
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || !testId) return;

        // Restore scroll position on mount
        if (analytics?.scroll_position) {
            scrollContainer.scrollTop = analytics?.scroll_position;
        }

        // Save scroll position on scroll
        const handleScroll = () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                if (scrollContainer) {
                    const position = scrollContainer.scrollTop;
                    updateAnalytics({ scroll_position: position });
                }
            }, 1000);
        };

        scrollContainer.addEventListener("scroll", handleScroll);

        return () => {
            scrollContainer.removeEventListener("scroll", handleScroll);
        };
    }, [testId]);

    const submit = async () => {
        if (!editor) return;
        if (onSubmit) await onSubmit(editor);
        setIsDirty(false);
        mutate();
    };

    useEffect(() => {
        // Calculate the number of pages based on elements with data-page attribute
        const calculatePages = () => {
            const pages = document.querySelectorAll("[data-page='true']");
            setNumPages(pages.length);
        };
        calculatePages();
        const observer = new MutationObserver(calculatePages);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, []);

    usePrompt({
        isDirty: isDirty,
        title: "Do you want to save the changes made to the report ?",
        description: "Your changes will be lost if you leave without saving.",
        confirmText: "Save",
        onConfirm: () => submit(),
    });

    return (
        <div
            className={cn(
                "flex-1 flex flex-col overflow-hidden editor",
                withFormatting && "editor-formatting"
            )}
        >
            <RichTextEditor editor={editor} className="overflow-hidden flex-1">
                <RichTextEditor.Toolbar className="sticky top-0 z-10 flex flex-col gap-4 px-4 py-2 bg-white border-t shadow-md">
                    <div className="flex gap-3 xl:gap-4">
                        <RichTextEditor.ControlsGroup className="controls-group">
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                            <RichTextEditor.Strikethrough />
                            <RichTextEditor.Highlight />
                            <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <FontFamilySelect />
                            <FontSizeSelect />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <RichTextEditor.H1 />
                            <RichTextEditor.H2 />
                            <RichTextEditor.H3 />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <RichTextEditor.BulletList />
                            <RichTextEditor.OrderedList />
                            <RichTextEditor.Subscript />
                            <RichTextEditor.Superscript />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <LinkButton />
                            <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <RichTextEditor.AlignLeft />
                            <RichTextEditor.AlignCenter />
                            <RichTextEditor.AlignRight />
                            <RichTextEditor.AlignJustify />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <LineHeightSelect />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="flex gap-2 border-r border-gray-200 pr-4">
                            <TableMenu />
                            <InsertImage />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="flex gap-2 border-r border-gray-200 pr-4 controls-group">
                            <RichTextEditor.Control
                                onClick={() =>
                                    setWithFormatting((prev) => !prev)
                                }
                                aria-label="Toggle formatting marks"
                                title="Toggle formatting marks"
                                className={cn(
                                    "flex items-center justify-center",
                                    withFormatting && "text-blue-500"
                                )}
                            >
                                ¶
                            </RichTextEditor.Control>
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup className="controls-group">
                            <RichTextEditor.Undo />
                            <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>
                    </div>
                </RichTextEditor.Toolbar>

                <div
                    ref={scrollContainerRef}
                    className="overflow-auto flex-1 max-h-full bg-gray-100 pt-6 pb-[100px]"
                >
                    <div
                        style={{
                            transform: `scale(${scale})`,
                            transformOrigin: "top center",
                        }}
                        className="overflow-auto"
                    >
                        <RichTextEditor.Content />
                    </div>
                </div>

                <ActionsMenu className="absolute right-0 mx-5 top-[53px] xl:top-20 mt-14" />
                <div className="bottom-[30px] right-0 absolute mx-5 flex justify-center items-center gap-2">
                    {isDirty ? (
                        <Button
                            className="btn-primary min-w-[170px]"
                            onClick={submit}
                            disabled={isSubmitting || !isAllowed("Report save")}
                        >
                            Save
                            {isSubmitting && (
                                <div className="size-5">
                                    <Loader />
                                </div>
                            )}
                        </Button>
                    ) : (
                        <SignatureReasonMenu
                            className="min-w-[170px]"
                            onSignSuccess={() => {
                                setHasShownSignedReportChangeAlert(false);
                            }}
                            onSignError={(error) => {
                                console.error("Failed to sign report:", error);
                                alert(
                                    "Failed to sign report. Please try again."
                                );
                            }}
                        />
                    )}
                </div>
            </RichTextEditor>

            <div className="h-[20px] bg-white px-4 flex justify-between items-center z-10 border-t">
                <span className="text-xs text-gray-500">
                    Page {currentPage} of {numPages}
                </span>
                <div className="w-[300px] flex items-center gap-2">
                    <input
                        type="range"
                        min={0.5}
                        max={1.5}
                        step={0.05}
                        value={scale}
                        onChange={(e) => setScale(Number(e.target.value))}
                        className="slider"
                    />
                    <span className="text-xs text-gray-500">
                        {(scale * 100).toFixed(0)}%
                    </span>
                </div>
            </div>
        </div>
    );
};
